import React, { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useApiCall } from '../../hooks/useApiCall'
import { extractProductId } from '../../utils/paths'
import { useAccount } from '../../contexts/AccountContext'
import { useCart } from '../../contexts/CartContext'
import { useToast } from '../../contexts/ToastContext'
import { useCatalogType } from '../../contexts/CatalogTypeContext'
import { 
  RiArrowLeftLine,
  RiHeart3Line,
  RiHeart3Fill,
  RiShare2Line,
  RiShoppingCartLine,
  RiSubtractLine,
  RiAddLine,
  RiTruckLine,
  RiSecurePaymentLine,
  RiRefund2Line,
  RiCheckLine,
  RiTimeLine,
  RiShieldCheckLine,
  RiMore2Line,
  RiErrorWarningLine
} from 'react-icons/ri'
import '../../styles/ProductPage.css'

export default function Product() {
  const { productSlug, id: legacyId } = useParams();
  const id = legacyId ?? extractProductId(productSlug);
  const navigate = useNavigate();
  const apiCall = useApiCall();
  const { account } = useAccount();
  const { refreshCounts } = useCart();
  const { showSuccess, showError } = useToast();
  const { catalogType } = useCatalogType();
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isTogglingWishlist, setIsTogglingWishlist] = useState(false);

  const [product, setProduct] = useState(null);

  // Fetch product data from API
  useEffect(() => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    apiCall('product/get', 'POST', { product_id: parseInt(id), type: catalogType }).then((response) => {
      if (response && response.success) {
        setProduct(response.data.product);
      } else {
        setError('Product not found');
      }
      setLoading(false);
    }).catch((err) => {
      setError('Failed to load product');
      setLoading(false);
    });
  }, [id, catalogType]);

  // Check if product is in wishlist
  useEffect(() => {
    if (!account || !id) return;
    
    apiCall('account/wishlist/check', 'POST', { product_id: parseInt(id) }, account).then((response) => {
      if (response && response.success) {
        setIsWishlisted(response.data.in_wishlist);
      }
    }).catch(() => {
      // Silently fail for wishlist check
    });
  }, [id, account]);

  // Show loading state
  if (loading) {
    return (
      <div className="product-page-loading">
        <p>Loading product...</p>
      </div>
    );
  }

  // Show error state
  if (error || !product) {
    return (
      <div className="product-page-error">
        <h2>Product Not Found</h2>
        <p>{error || 'The product you are looking for could not be found.'}</p>
        <button onClick={() => navigate('/products')} style={{ marginTop: 12 }} className="btn-primary">
          Back to Products
        </button>
      </div>
    );
  }

  // Transform API product data to match component expectations
  const transformedProduct = {
    id: product.id,
    name: product.name,
    shortDescription: product.description ? product.description.substring(0, 100) + '...' : '',
    description: product.description || '',
    comingSoon: product.coming_soon || false,
    orderOnly: product.order_only || false,
    price: {
      curr: product.price?.curr || '$',
      amount: product.sale_price && product.sale_price > 0 ? product.sale_price : product.price?.amount || 0,
      originalAmount: product.sale_price && product.sale_price > 0 ? product.price?.amount : null,
      discount: product.sale_price && product.sale_price > 0 ? 
        Math.round(((product.price?.amount - product.sale_price) / product.price?.amount) * 100) : 0
    },
    images: product.images && product.images.length > 0 ? product.images : [
      { 
        id: 1, 
        url: '/data/images/placeholder.jpg',
        alt: product.name
      }
    ],
    modifiers: product.modifiers && typeof product.modifiers === 'object' ? 
      Object.entries(product.modifiers).map(([key, value]) => ({
        id: key.toLowerCase().replace(/\s+/g, '_'),
        name: key,
        required: false,
        options: Array.isArray(value) ? 
          value.map(opt => ({
            id: `${key.toLowerCase()}_${opt.id}`,
            name: opt.name,
            description: opt.description || '',
            amount: opt.amount || 0,
            inStock: true
          })) : 
          []
      })) : [],
    features: product.features ? product.features.split('\n').filter(f => f.trim()) : [],
    specifications: {
      ...(product.product_id ? { 'Product ID': product.product_id } : {}),
      ...(product.brand ? { 'Brand': product.brand } : {}),
      ...(product.stock !== 999 ? {
          'Stock': product.stock || 0,    
      } : {}), // Hide stock if unlimited
      'In stock': product.in_stock ? 'Yes' : 'No'
    },
    shipping: {
      estimatedDays: '3-5 business days',
      freeShipping: true,
      returnable: true,
      returnDays: 30
    },
    inStock: product.in_stock || false,
    stockCount: product.stock || 0,
    brand: product.brand || '',
    sku: `PROD-${product.id}`,
    category: {
      id: product.category_id,
      name: product.category_name || 'Uncategorized',
      slug: product.category_name ? 
        product.category_name
          .toLowerCase()
          .replace(/[^a-z0-9\s&]/g, '')
          .replace(/\s*&\s*/g, '-and-')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '') 
        : 'uncategorized'
    },
    categoryPath: product.category_path || []
  };

  // Helper functions
  const calculateTotalPrice = () => {
    // Modifier amount replaces base price (versions/variants)
    const modifierAmounts = Object.values(selectedModifiers).filter(opt => opt && opt.amount > 0);
    const unitPrice = modifierAmounts.length > 0 ? modifierAmounts[0].amount : transformedProduct.price.amount;
    return unitPrice * quantity;
  };

  const handleModifierChange = (modifierId, option) => {
    setSelectedModifiers(prev => ({
      ...prev,
      [modifierId]: option
    }));
  };

  const handleQuantityChange = (delta) => {
    setQuantity(prev => Math.max(1, Math.min(transformedProduct.stockCount, prev + delta)));
  };

  const handleAddToCart = async () => {
    if (!account) {
      showError('Please sign in to add items to your cart');
      navigate('/sign-in?ref=cart');
      return;
    }

    if (transformedProduct.comingSoon) {
      showError('This product is coming soon and cannot be ordered yet');
      return;
    }

    // Check if all required modifiers are selected
    const requiredModifiers = transformedProduct.modifiers.filter(modifier => modifier.required);
    const missingRequired = requiredModifiers.filter(modifier => !selectedModifiers[modifier.id]);

    if (missingRequired.length > 0) {
      showError(`Please select: ${missingRequired.map(m => m.name).join(', ')}`);
      return;
    }

    if (!transformedProduct.orderOnly && !transformedProduct.inStock) {
      showError('This product is currently out of stock');
      return;
    }

    setIsAddingToCart(true);

    try {
      // Build modifiers array with selected options
      const modifiers = Object.entries(selectedModifiers).map(([modifierId, option]) => {
        const modifier = transformedProduct.modifiers.find(m => m.id === modifierId);
        return {
          name: modifier.name,
          option: {
            item: option.name,
            amount: option.amount || 0
          }
        };
      });

      // Build options JSON
      const options = modifiers.length > 0 ? JSON.stringify({ modifiers }) : null;

      const response = await apiCall('account/cart/add', 'POST', {
        product_id: transformedProduct.id,
        qty: quantity,
        options: options
      }, account);

      if (response && response.success) {
        if (transformedProduct.orderOnly) {
          showSuccess('Added to cart! This is an Order Only item - we\'ll contact you with pricing after your order is placed.', 6000, () => navigate('/cart'));
        } else {
          showSuccess('Product added to cart successfully!', 4000, () => navigate('/cart'));
        }
        refreshCounts();
      } else {
        showError(response?.message || 'Failed to add product to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      showError('Failed to add product to cart');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleWishlistToggle = async () => {
    if (!account) {
      showError('Please sign in to manage your wishlist');
      navigate('/sign-in?ref=wishlist');
      return;
    }

    setIsTogglingWishlist(true);

    try {
      const endpoint = isWishlisted ? 'account/wishlist/remove' : 'account/wishlist/add';
      
      let requestData = { product_id: transformedProduct.id };
      
      // Add modifiers when adding to wishlist
      if (!isWishlisted) {
        const modifiers = Object.entries(selectedModifiers).map(([modifierId, option]) => {
          const modifier = transformedProduct.modifiers.find(m => m.id === modifierId);
          return {
            name: modifier.name,
            option: {
              item: option.name,
              amount: option.amount || 0
            }
          };
        });
        
        if (modifiers.length > 0) {
          requestData.options = JSON.stringify({ modifiers });
        }
      }
      
      const response = await apiCall(endpoint, 'POST', requestData, account);

      if (response && response.success) {
        setIsWishlisted(!isWishlisted);
        showSuccess(isWishlisted ? 'Removed from wishlist' : 'Added to wishlist', 3000, () => navigate('/wishlist'));
        refreshCounts(); // Refresh counts after toggling wishlist
      } else {
        showError(response?.message || `Failed to ${isWishlisted ? 'remove from' : 'add to'} wishlist`);
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      showError(`Failed to ${isWishlisted ? 'remove from' : 'add to'} wishlist`);
    } finally {
      setIsTogglingWishlist(false);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: transformedProduct.name,
      text: `Check out ${transformedProduct.name} - ${transformedProduct.shortDescription}`,
      url: window.location.href
    };

    try {
      // Try Web Share API first (mobile browsers)
      if (navigator.share) {
        await navigator.share(shareData);
        showSuccess('Product shared successfully!');
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        showSuccess('Product link copied to clipboard!');
      }
    } catch (error) {
      // Final fallback: Manual copy prompt
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showSuccess('Product link copied to clipboard!');
    }
  };



  return (
    <div className="product-page">
      {/* Breadcrumb Navigation */}
      <div className="product-breadcrumb">
        <button className="breadcrumb-back" onClick={() => navigate(-1)}>
          <RiArrowLeftLine />
          Back
        </button>
        <div className="breadcrumb-path">
          <Link to="/">Home</Link>
          <span className="separator">›</span>
          <Link to="/products">Products</Link>
          
          {/* Show full category path */}
          {transformedProduct.categoryPath && transformedProduct.categoryPath.length > 0 && (
            transformedProduct.categoryPath.map((cat, index) => (
              <React.Fragment key={cat.id}>
                <span className="separator">›</span>
                <Link to={`/products/category/${cat.id}/${cat.slug}`}>
                  {cat.name}
                </Link>
              </React.Fragment>
            ))
          )}
          
          <span className="separator">›</span>
          <span className="current">{transformedProduct.name}</span>
        </div>
      </div>

      {/* Main Product Container */}
      <div className="product-container">
        {/* Product Images */}
        <div className="product-images">
          <div className="main-image">
            <img 
              src={transformedProduct.images[currentImageIndex]?.url} 
              alt={transformedProduct.images[currentImageIndex]?.alt || transformedProduct.name}
            />
            <div className="image-dots">
              {transformedProduct.images.map((_, index) => (
                <button
                  key={index}
                  className={`image-dot ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                />
              ))}
            </div>
          </div>
          
          <div className="thumbnail-images">
            {transformedProduct.images.map((image, index) => (
              <button
                key={image.id}
                className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                onClick={() => setCurrentImageIndex(index)}
              >
                <img src={image.url} alt={image.alt} />
              </button>
            ))}
          </div>
        </div>

        {/* Product Details */}
        <div className="product-details">
          <div className="product-header">
            <div className="product-brand">{transformedProduct.brand}</div>
            <h1 className="product-title">{transformedProduct.name}</h1>
            <p className="product-short-desc">{transformedProduct.shortDescription}</p>
          </div>

          {/* Pricing */}
          <div className="product-pricing">
            {transformedProduct.orderOnly ? (
              <span className="order-only-badge">Order Only</span>
            ) : transformedProduct.comingSoon ? (
              <span className="coming-soon-badge">Coming Soon</span>
            ) : (
              <>
                <span className="price-main">{transformedProduct.price.curr} {calculateTotalPrice().toFixed(2)}</span>
                {transformedProduct.price.originalAmount && (
                  <span className="price-original">{transformedProduct.price.curr}{(transformedProduct.price.originalAmount * quantity).toFixed(2)}</span>
                )}
                {transformedProduct.price.discount > 0 && (
                  <span className="price-discount">{transformedProduct.price.discount}% OFF</span>
                )}
              </>
            )}
          </div>

          {/* Stock Status */}
          <div className="stock-status">
            {transformedProduct.orderOnly ? (
              <div className="order-only-status">
                <RiShoppingCartLine className="stock-icon" />
                <span>Order Only. Pricing will be confirmed after ordering</span>
              </div>
            ) : transformedProduct.comingSoon ? (
              <div className="coming-soon-status">
                <RiTimeLine className="stock-icon" />
                <span>This product will be available soon</span>
              </div>
            ) : transformedProduct.inStock ? (
              <div className="in-stock">
                <RiCheckLine className="stock-icon" />
                <span>In stock {transformedProduct.stockCount !== 999 ? `(${transformedProduct.stockCount} available)` : null}</span>
              </div>
            ) : (
              <div className="out-of-stock">
                <RiErrorWarningLine className="stock-icon" />
                <span>Out of stock</span>
              </div>
            )}
          </div>

          {/* Product Modifiers */}
          <div className="product-options">
            {transformedProduct.modifiers.map(modifier => (
              <div key={modifier.id} className="option-group">
                <label className="option-label">
                  {modifier.name} {modifier.required && <span style={{color: '#dc2626'}}>*</span>}
                </label>
                
                {modifier.id === 'color' ? (
                  <div className="color-options">
                    {modifier.options.map(option => (
                      <button
                        key={option.id}
                        className={`color-option ${
                          selectedModifiers[modifier.id]?.id === option.id ? 'selected' : ''
                        } ${!option.inStock ? 'out-of-stock' : ''}`}
                        onClick={() => option.inStock && handleModifierChange(modifier.id, option)}
                        disabled={!option.inStock}
                      >
                        <div 
                          className="color-swatch" 
                          style={{ backgroundColor: option.color }}
                        />
                        <span className="color-name">
                          {option.name}
                          {option.amount > 0 && ` - $${option.amount}`}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <select 
                    className="modifier-dropdown"
                    value={selectedModifiers[modifier.id]?.id || ''}
                    onChange={(e) => {
                      const option = modifier.options.find(opt => opt.id === e.target.value);
                      if (option) {
                        handleModifierChange(modifier.id, option);
                      }
                    }}
                  >
                    <option value="">Select {modifier.name}</option>
                    {modifier.options.map(option => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                        {option.description ? ` - ${option.description}` : ''}
                        {option.amount > 0 ? ` - $${option.amount}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>

          {/* Order Only Notice */}
          {transformedProduct.orderOnly && (
            <div className="order-only-notice">
              <RiErrorWarningLine className="order-only-notice-icon" />
              <div className="order-only-notice-text">
                <strong>Order Only item</strong>
                <p>This product is available by order. Add it to your cart and place your order - our team will contact you with pricing and availability confirmation.</p>
              </div>
            </div>
          )}

          {/* Product Actions */}
          <div className="product-actions">
            {!transformedProduct.orderOnly && (
              <div className="quantity-selector">
                <label>Quantity:</label>
                <div className="quantity-controls">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                  >
                    <RiSubtractLine />
                  </button>
                  <div className="quantity-display">{quantity}</div>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= transformedProduct.stockCount}
                  >
                    <RiAddLine />
                  </button>
                </div>
              </div>
            )}

            <div className="action-buttons">
              <button
                className={`btn-add-cart${transformedProduct.orderOnly ? ' order-only' : ''}`}
                onClick={handleAddToCart}
                disabled={transformedProduct.comingSoon || (!transformedProduct.orderOnly && !transformedProduct.inStock) || isAddingToCart}
              >
                <RiShoppingCartLine />
                {transformedProduct.comingSoon ? 'Coming Soon' :
                 isAddingToCart ? 'Adding...' :
                 transformedProduct.orderOnly ? 'Add to Cart' :
                 `Add to Cart - ${transformedProduct.price.curr} ${calculateTotalPrice().toFixed(2)}`}
              </button>
              <button
                className={`btn-wishlist ${isWishlisted ? 'active' : ''}`}
                onClick={handleWishlistToggle}
                disabled={isTogglingWishlist}
                title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
              >
                {isWishlisted ? <RiHeart3Fill /> : <RiHeart3Line />}
              </button>
              <button
                className="btn-product-share"
                onClick={handleShare}
                title="Share this product"
              >
                <RiShare2Line />
              </button>
            </div>
          </div>

          {/* Shipping Info */}
          {/* <div className="shipping-info">
            <div className="shipping-item">
              <RiTruckLine className="shipping-icon" />
              <div className="shipping-text">
                <strong>Free Shipping</strong>
                <span>Estimated delivery: {product.shipping.estimatedDays}</span>
              </div>
            </div>
            <div className="shipping-item">
              <RiRefund2Line className="shipping-icon" />
              <div className="shipping-text">
                <strong>{product.shipping.returnDays}-Day Returns</strong>
                <span>Free returns on eligible items</span>
              </div>
            </div>
            <div className="shipping-item">
              <RiShieldCheckLine className="shipping-icon" />
              <div className="shipping-text">
                <strong>Warranty Included</strong>
                <span>Manufacturer warranty included</span>
              </div>
            </div>
          </div> */}
        </div>
      </div>

      {/* Product Information Tabs */}
      <div className="product-tabs">
        <div className="tab-section">
          <h3>Product description</h3>
          <p className="product-description">{transformedProduct.description}</p>
        </div>

        {transformedProduct.features && transformedProduct.features.length > 0 && (
        <div className="tab-section">
          <h3>Key features</h3>
          <ul className="product-features">
            {transformedProduct.features.map((feature, index) => (
              <li key={index}>
                <RiCheckLine className="feature-icon" />
                {feature}
              </li>
            ))}
          </ul>
        </div>
        )}

        <div className="tab-section">
          <h3>Specifications</h3>
          <div className="product-specs">
            {Object.entries(transformedProduct.specifications)
              .filter(([key, value]) => value !== '' && value !== null && value !== undefined)
              .map(([key, value]) => (
              <div key={key} className="spec-row">
                <div className="spec-name">{key}:</div>
                <div className="spec-value">{value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
